import { customerApi } from "api/customer.api";
import { staffApi } from "api/staff.api";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Customer } from "types/customer";
import { Staff } from "types/staff";

export type RemoteOption = { label: string; value: number };

export const fetchCustomerOptions = async (keyword: string): Promise<RemoteOption[]> => {
  const res = await customerApi.findAll({ page: 1, limit: 20, search: keyword });
  const customers: Customer[] = res.data.customers || [];
  return customers.map((customer) => ({ label: customer.name, value: customer.id }));
};

export const fetchStaffOptions = async (keyword: string): Promise<RemoteOption[]> => {
  const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
  const staffs: Staff[] = res.data.staffs || [];
  return staffs.map((staff) => ({ label: staff.name || staff.username, value: staff.id }));
};

// Options cho ô chọn có tìm kiếm từ API (dùng cho bộ lọc): giữ lại option đang chọn khi tìm từ khoá khác
export const useRemoteOptions = (fetcher: (keyword: string) => Promise<RemoteOption[]>, enabled = true) => {
  const [options, setOptions] = useState<RemoteOption[]>([]);
  const selectedRef = useRef<RemoteOption>();
  const optionsRef = useRef<RemoteOption[]>([]);
  optionsRef.current = options;

  const load = useCallback(
    async (keyword: string) => {
      const list = await fetcher(keyword);
      const selected = selectedRef.current;
      setOptions(selected && !list.some((option) => option.value === selected.value) ? [selected, ...list] : list);
    },
    [fetcher],
  );

  const onSearch = useMemo(() => debounce((keyword: string) => load(keyword), 500), [load]);

  useEffect(() => {
    if (!enabled) return;
    load("");
    return () => onSearch.cancel();
  }, [enabled, load, onSearch]);

  const select = useCallback((value?: number) => {
    selectedRef.current = value ? optionsRef.current.find((option) => option.value === value) : undefined;
  }, []);

  return { options, onSearch, select };
};
